import React from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import {
  LayoutDashboard, Layers, Package, Truck, Cog, FlaskConical,
  Sparkles, BookOpen, LogOut, ShieldCheck, Coins, Settings
} from "lucide-react";

const NAV = [
  { to: "/", icon: LayoutDashboard, label: "Executive", roles: ["admin","sustainability","production","procurement","auditor","supplier"] },
  { to: "/batches", icon: Layers, label: "Batches", roles: ["admin","sustainability","production","auditor"] },
  { to: "/denominations", icon: Coins, label: "Denominations", roles: ["admin","sustainability","production","auditor"] },
  { to: "/lifecycle", icon: Truck, label: "Lifecycle Twin", roles: ["admin","sustainability","production","auditor"] },
  { to: "/suppliers", icon: Package, label: "Suppliers & Evidence", roles: ["admin","sustainability","procurement","supplier","auditor"] },
  { to: "/scenarios", icon: FlaskConical, label: "Scenario Simulator", roles: ["admin","sustainability","production"] },
  { to: "/rulebook", icon: BookOpen, label: "PCF Rulebook", roles: ["admin","sustainability","auditor"] },
  { to: "/ai", icon: Sparkles, label: "AI Insights", roles: ["admin","sustainability","auditor"] },
  { to: "/admin", icon: Settings, label: "Admin Console", roles: ["admin"] },
];

const RoleBadge = ({ role }) => {
  const map = {
    admin: "bg-slate-900 text-white",
    sustainability: "bg-emerald-700 text-white",
    production: "bg-amber-700 text-white",
    procurement: "bg-slate-700 text-white",
    auditor: "bg-blue-700 text-white",
    supplier: "bg-zinc-600 text-white",
  };
  return (
    <span data-testid="role-badge" className={`inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.18em] ${map[role] || "bg-slate-700 text-white"}`}>
      <ShieldCheck className="w-3 h-3" /> {role}
    </span>
  );
};

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  if (!user) return null;

  const items = NAV.filter((n) => n.roles.includes(user.role));

  return (
    <div className="min-h-screen flex bg-white">
      {/* Sidebar */}
      <aside data-testid="sidebar" className="w-64 shrink-0 border-r border-slate-200 bg-white flex flex-col sticky top-0 h-screen">
        <div className="px-6 py-5 border-b border-slate-200">
          <div className="overline text-emerald-800">Bangko Sentral</div>
          <div className="font-heading text-xl font-extrabold tracking-tight text-slate-900 mt-1">PCF Platform</div>
          <div className="text-[11px] text-slate-500 mt-1">Currency Carbon Control Layer</div>
        </div>

        <nav className="flex-1 overflow-y-auto py-4">
          {items.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`nav-${n.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              className={({ isActive }) =>
                `flex items-center gap-3 px-6 py-2.5 text-sm transition-colors ${
                  isActive
                    ? "bg-emerald-50 text-emerald-900 border-l-2 border-emerald-700 font-semibold"
                    : "text-slate-700 hover:bg-slate-50 border-l-2 border-transparent"
                }`
              }
            >
              <n.icon className="w-4 h-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-slate-200 p-4">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-emerald-700 text-white grid place-items-center font-semibold">
              {(user.full_name || "U").charAt(0)}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-slate-900 truncate">{user.full_name}</div>
              <RoleBadge role={user.role} />
            </div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={() => { logout(); navigate("/login"); }}
            className="mt-3 w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.18em] py-2 border border-slate-300 hover:bg-slate-900 hover:text-white transition-colors"
          >
            <LogOut className="w-3.5 h-3.5" /> Sign out
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="h-14 border-b border-slate-200 bg-white/70 backdrop-blur-xl sticky top-0 z-10 flex items-center justify-between px-8">
          <div className="flex items-center gap-2">
            <Cog className="w-4 h-4 text-emerald-700" />
            <span className="overline text-slate-500">Currency Sustainability Control Layer</span>
          </div>
          <div className="text-xs text-slate-500 font-mono">ISO 14067 · GHG Protocol</div>
        </header>
        <div data-testid="page-container" className="p-6 lg:p-10 max-w-[1600px] mx-auto">{children}</div>
      </main>
    </div>
  );
};

export default Layout;
